import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, Button, Space, Alert, Spin, Tag, Statistic, Divider } from 'antd'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ApiOutlined,
  ClockCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons'

const { Title, Text } = Typography

const WebhookTest = () => {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [lastCheck, setLastCheck] = useState(null)
  const [error, setError] = useState(null)

  const testWebhookEndpoint = async () => {
    setLoading(true)
    setError(null)
    const startTime = Date.now()

    try {
      // Testa o endpoint público que não requer autenticação
      const response = await fetch(`${import.meta.env.VITE_API_URL}/webhook/test-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const responseTime = Date.now() - startTime

      // Verifica se o endpoint existe (qualquer resposta diferente de 404)
      if (response.status === 404) {
        setStatus({
          success: false,
          message: 'Endpoint não encontrado',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          responseTime
        })
      } else {
        // Endpoint existe (mesmo que retorne erro de autenticação)
        setStatus({
          success: true,
          message: response.status === 401 || response.status === 400 ?
            'Endpoint existe (erro de autenticação esperado)' :
            'Endpoint acessível',
          statusCode: response.status,
          timestamp: new Date().toISOString(),
          responseTime
        })
      }
    } catch (err) {
      setStatus({
        success: false,
        message: err.message,
        statusCode: null,
        timestamp: new Date().toISOString(),
        responseTime: null
      })
      setError(err.message)
    } finally {
      setLoading(false)
      setLastCheck(new Date())
    }
  }

  useEffect(() => {
    testWebhookEndpoint()
  }, [])

  const getStatusIcon = () => {
    if (loading) return <Spin size="small" />
    return status?.success ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp).toLocaleString('pt-BR')
  }

  const formatLastCheck = (date) => {
    if (!date) return 'Nunca'
    return date.toLocaleString('pt-BR')
  }

  const getStatusMessage = () => {
    if (loading) return 'Verificando...'
    if (!status) return 'Não testado'
    return status.success ? 'Endpoint Encontrado' : 'Endpoint Não Encontrado'
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={2}>
                  <GlobalOutlined /> Teste de Conectividade do Webhook
                </Title>
                <Text type="secondary">
                  Verifica se o endpoint do webhook existe e está acessível (sem autenticação)
                </Text>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Status do Endpoint"
                      value={getStatusMessage()}
                      prefix={getStatusIcon()}
                      valueStyle={{
                        color: status?.success ? '#52c41a' : '#ff4d4f',
                        fontSize: '16px'
                      }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Código HTTP"
                      value={status?.statusCode || 'N/A'}
                      prefix={<ApiOutlined />}
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Tempo de Resposta"
                      value={status?.responseTime ? `${status.responseTime}ms` : 'N/A'}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ fontSize: '14px' }}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Última Verificação"
                      value={formatLastCheck(lastCheck)}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ fontSize: '12px' }}
                    />
                  </Card>
                </Col>
              </Row>

              {status && (
                <Alert
                  message={status.success ? 'Endpoint Acessível' : 'Problema de Conectividade'}
                  description={
                    <div>
                      <p><strong>Mensagem:</strong> {status.message}</p>
                      <p><strong>Código HTTP:</strong> {status.statusCode || 'N/A'}</p>
                      <p><strong>Timestamp:</strong> {formatTimestamp(status.timestamp)}</p>
                      {status.responseTime && (
                        <p><strong>Tempo de Resposta:</strong> {status.responseTime}ms</p>
                      )}
                    </div>
                  }
                  type={status.success ? 'success' : 'error'}
                  showIcon
                />
              )}

              {status?.data?.documentation && (
                <Card title="📚 Documentação da API" size="small" style={{ marginTop: '16px' }}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Text strong>Endpoint:</Text> <Tag color="blue">{status.data.documentation.endpoint}</Tag>
                      <Text strong style={{ marginLeft: '16px' }}>Método:</Text> <Tag color="green">{status.data.documentation.method}</Tag>
                    </Col>

                    <Col span={24}>
                      <Text strong>Descrição:</Text>
                      <p style={{ marginTop: '8px', marginBottom: '16px' }}>{status.data.documentation.description}</p>
                    </Col>

                    <Col span={24}>
                      <Title level={5}>🔐 Autenticação</Title>
                      <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                        <p><strong>Requerida:</strong> {status.data.documentation.authentication.required ? 'Sim' : 'Não'}</p>
                        <p><strong>Tipo:</strong> {status.data.documentation.authentication.type}</p>
                        {status.data.documentation.authentication.header && (
                          <p><strong>Header:</strong> <code>{status.data.documentation.authentication.header}</code></p>
                        )}
                        {status.data.documentation.authentication.description && (
                          <p><strong>Nota:</strong> {status.data.documentation.authentication.description}</p>
                        )}
                      </div>
                    </Col>

                    <Col span={24}>
                      <Title level={5}>📝 Exemplos de Uso</Title>
                      <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                        {status.data.documentation.examples.curl && (
                          <div style={{ marginBottom: '12px' }}>
                            <Text strong>cURL:</Text>
                            <pre style={{ backgroundColor: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', overflow: 'auto' }}>
                              {status.data.documentation.examples.curl}
                            </pre>
                          </div>
                        )}
                        {status.data.documentation.examples.javascript && (
                          <div style={{ marginBottom: '12px' }}>
                            <Text strong>JavaScript:</Text>
                            <pre style={{ backgroundColor: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', overflow: 'auto' }}>
                              {status.data.documentation.examples.javascript}
                            </pre>
                          </div>
                        )}
                        {status.data.documentation.examples.python && (
                          <div>
                            <Text strong>Python:</Text>
                            <pre style={{ backgroundColor: '#fff', padding: '8px', borderRadius: '4px', fontSize: '12px', overflow: 'auto' }}>
                              {status.data.documentation.examples.python}
                            </pre>
                          </div>
                        )}
                      </div>
                    </Col>

                    {status.data.documentation.usage && (
                      <Col span={24}>
                        <Title level={5}>💡 Informações de Uso</Title>
                        <div style={{ backgroundColor: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                          <p><strong>Propósito:</strong> {status.data.documentation.usage.purpose}</p>
                          <p><strong>Ideal para:</strong> {status.data.documentation.usage.ideal_for}</p>
                          <p><strong>Nota de Segurança:</strong> {status.data.documentation.usage.security_note}</p>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card>
              )}

              {error && (
                <Alert
                  message="Erro de Conectividade"
                  description={error}
                  type="error"
                  showIcon
                />
              )}

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={testWebhookEndpoint}
                  size="large"
                >
                  {loading ? 'Testando...' : 'Testar Conectividade'}
                </Button>
              </div>

              <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                <Title level={4}>Informações do Teste</Title>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text strong>Endpoint Testado:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="blue">/webhook/test-public</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Método:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="green">POST</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Autenticação:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="green">Não Requerida</Tag>
                  </Col>

                  <Col span={12}>
                    <Text strong>Objetivo:</Text>
                  </Col>
                  <Col span={12}>
                    <Tag color="purple">Verificar Existência do Endpoint</Tag>
                  </Col>
                </Row>

                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">
                    <strong>Nota:</strong> Este endpoint é público e não requer autenticação.
                    Um código 200 indica que o webhook está funcionando corretamente.
                  </Text>
                </div>
              </Card>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default WebhookTest